import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Row, Descriptions, Card, Col, Spin, message, Alert } from 'antd';
import { fetchCaseEnachMandataDetails, fetchCaseEnachMandateStatus } from 'store/actions';
import { STATE_STATUS } from 'constants';
import { splitAndCapitalize } from '../../components/helper';
import { Map } from 'immutable';
import { CaseService } from '../../services';
import NachMandateForm from '../../forms/nach-mandate-form';
import NachDetailsDrawer from '../../components/nach-details-drawer';
import { BANK_ACCOUNT_TYPES } from '../../constants/enums';
import { NachMandateStatusRecord } from '../../records';
import {
    findResultForType,
    getCategoryDisplayLabel,
    getCategorySubtitle,
    getDesignSectionTypes,
    toPlainJs,
    getDefaultMandateTypeFromCategory,
    resolveTotalEmiCount,
    resolveEmiDateForNachRequest,
    getNachMandateDetailValue,
    isLoanAbove1CrFromSanctionLimit,
} from 'utilities/nach-mandate-utils';

class NachMandate extends React.Component {
    state = {
        visible: false,
        triggerDetails: {},
        triggerNachLoader: false,
        formValuesBySection: {},
        selectedEmiDate: null,
    };

    componentDidMount() {
        this.loadStatus();
    }

    componentDidUpdate(prevProps) {
        const prevEmi = prevProps.nachMandateStatusResponse.get('emi_dates');
        const currEmi = this.props.nachMandateStatusResponse.get('emi_dates');
        if (
            prevEmi !== currEmi &&
            currEmi &&
            currEmi.size &&
            this.state.selectedEmiDate == null
        ) {
            this.setState({ selectedEmiDate: currEmi.get(0) });
        }
        if (
            prevProps.nachData.get('status') === STATE_STATUS.FETCHING &&
            this.props.nachData.get('status') === STATE_STATUS.FETCHED
        ) {
            this.setState({ visible: true });
        }
    }

    nachStatus = () => this.props.nachMandateStatusResponse;

    isFetched = () => this.nachStatus().get('status') === STATE_STATUS.FETCHED;

    getJourney = () => toPlainJs(this.nachStatus().get('nach_journey'));

    getPlainResults = () => {
        const results = this.nachStatus().get('data');
        const list = Array.isArray(results) ? results : toPlainJs(results) || [];
        return list.map((r) => toPlainJs(r));
    };

    loadStatus = (emiDate) => {
        this.props.fetchCaseEnachMandateStatus(this.props.caseId);
    };

    onClose = () => {
        this.setState({ visible: false, triggerDetails: {}, formValuesBySection: {} });
    };

    resolveSectionEmiDate = (nachRequestType, payloadEmiDate) => {
        const status = this.nachStatus();
        const journey = this.getJourney();
        const category =
            journey && journey.categories ? journey.categories[nachRequestType] : null;
        return resolveEmiDateForNachRequest({
            nachRequestType,
            payloadEmiDate,
            category,
            emiDates: toPlainJs(status.get('emi_dates')) || [],
            resultsList: this.getPlainResults(),
            journey,
            selectedEmiDate: this.state.selectedEmiDate,
        });
    };

    onSubmit = (payload, formValues, sectionKey) => {
        const emi_date = this.resolveSectionEmiDate(sectionKey, payload.emi_date);
        if (
            (sectionKey === 'EMI-1' || sectionKey === 'EMI-2') &&
            (emi_date == null || emi_date === '')
        ) {
            message.error('EMI date is not available for this section. Refresh NACH status and try again.');
            return;
        }

        const requestPayload = {
            mandate_type: payload.mandate_type,
            bank_account_id: payload.bank_account_id,
            associate_id: payload.associate_id,
            nach_request_type: payload.nach_request_type,
        };
        if (emi_date != null && emi_date !== '') {
            requestPayload.emi_date = emi_date;
        }

        this.setState({
            triggerDetails: { ...payload, emi_date },
            formValuesBySection: { ...this.state.formValuesBySection, [sectionKey]: formValues },
        });
        this.props.fetchCaseEnachMandataDetails(this.props.caseId, requestPayload);
    };

    getBankColumns = (bank_details) => {
        const bank = bank_details || {};
        const branch = bank.bank_branch || {};
        return Map({
            account_number: bank.account_number || '--',
            account_holder_name: bank.account_holder_name || '--',
            account_type:
                bank.account_type != null
                    ? BANK_ACCOUNT_TYPES.findKey((val) => val === bank.account_type)
                    : '--',
            account_limit: bank.account_limit != null ? bank.account_limit : '--',
            ifsc: branch.ifsc_code || '--',
            bank_name: branch.bank_name || '--',
            bank_branch: branch.address || '--',
        });
    };

    getBankDetails = () => {
        const status = this.nachStatus();
        if (!status || !status.get('status')) return null;

        const bankJs = toPlainJs(status.get('bank_account'));
        const fetchStatus = status.get('status');

        return (
            <Card className="padless-card bank-account-list-card" title="Bank Accounts">
                {fetchStatus === STATE_STATUS.FETCH_FAILED ? (
                    <div>Something Went Wrong</div>
                ) : (
                    <Descriptions bordered column={1}>
                        {[STATE_STATUS.FETCHING, STATE_STATUS.UNFETCHED].includes(fetchStatus) ? (
                            <Spin />
                        ) : (
                            this.getBankColumns(bankJs).map((val, key) => (
                                <Descriptions.Item key={key} label={splitAndCapitalize('_', key)}>
                                    {val}
                                </Descriptions.Item>
                            ))
                        )}
                    </Descriptions>
                )}
            </Card>
        );
    };

    findDrawerResponse = () => {
        const { triggerDetails } = this.state;
        if (!triggerDetails.nach_request_type) return null;
        const row = findResultForType(
            this.getPlainResults(),
            triggerDetails.nach_request_type,
            triggerDetails.emi_date
        );
        return row ? new NachMandateStatusRecord(row) : null;
    };

    triggerEnach = (payload = {}) => {
        const bank = toPlainJs(this.nachStatus().get('bank_account'));
        if (!bank) {
            message.error('Bank account details are missing.');
            return Promise.reject();
        }

        const today = new Date();
        const collection_date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const trigger = { ...this.state.triggerDetails, ...payload };

        const end_date = getNachMandateDetailValue(this.props.nachData, 'end_date');
        const frequency = getNachMandateDetailValue(this.props.nachData, 'frequency');

        const body =
            payload._formData instanceof FormData
                ? payload._formData
                : {
                      nach_request_type: trigger.nach_request_type,
                      nach_mode: trigger.nach_mode,
                      nach_type: trigger.mandate_type,
                      associate: trigger.associate_id || trigger.associate,
                      account: bank.account_number,
                      collection_date,
                      amount: trigger.amount || 10000000,
                      emi_date: trigger.emi_date,
                      cheque_details: trigger.cheque_details,
                      end_date,
                      frequency,
                  };

        this.setState({ triggerNachLoader: true });
        return CaseService.triggerEnach(this.props.caseId, body)
            .then((resp) => {
                message.success(
                    (resp.data && resp.data.message) || 'Nach Created Successfully'
                );
                this.loadStatus(trigger.emi_date);
                this.setState({
                    triggerNachLoader: false,
                    triggerDetails: {},
                    visible: false,
                    formValuesBySection: {},
                });
            })
            .catch((err) => {
                message.error(this.extractApiErrorMessage(err) || 'Something went wrong!');
                this.setState({ triggerNachLoader: false });
                return Promise.reject(err);
            });
    };

    /**
     * Pulls a readable message from API errors, including DRF field errors
     * like { eml_file: ["EML file is required ..."] }.
     */
    extractApiErrorMessage = (err) => {
        if (!err) return null;
        if (typeof err === 'string') return err;

        const data =
            (err.Message && err.Message.data) ||
            (err.response && err.response.data) ||
            err.data ||
            err;

        if (typeof data === 'string') return data;
        if (data && typeof data === 'object') {
            if (data.message) return data.message;
            if (data.detail) return data.detail;
            const firstKey = Object.keys(data)[0];
            if (firstKey != null) {
                const val = data[firstKey];
                if (Array.isArray(val) && val.length) return String(val[0]);
                if (typeof val === 'string') return val;
            }
        }
        return err.message || null;
    };

    renderProceedValidation = () => {
        const v = toPlainJs(this.nachStatus().get('proceed_validation'));
        if (!v) return null;

        return (
            <Card className="mt-10" title="Proceed from PS Kit Review">
                {v.blockers && v.blockers.length > 0 ? (
                    <ul className="pl-20" style={{ marginBottom: 16 }}>
                        {v.blockers.map((b, i) => (
                            <li key={i}>{b.message}</li>
                        ))}
                    </ul>
                ) : null}
                {v.can_proceed && v.is_ready_message ? (
                    <Alert type="success" message={v.is_ready_message} showIcon />
                ) : null}
            </Card>
        );
    };

    renderNachSection = (nachRequestType) => {
        const {
            caseId,
            nachData,
            schemeType,
            caseAssociates,
            user,
            currentCaseTaskStageId,
            caseDetail,
        } = this.props;
        const status = this.nachStatus();
        const journey = this.getJourney();

        if (!journey || !journey.categories || !journey.categories[nachRequestType]) return null;

        const category = journey.categories[nachRequestType];
        const emiList = toPlainJs(status.get('emi_dates')) || [];
        const emiDate = this.resolveSectionEmiDate(nachRequestType);

        const resultsList = this.getPlainResults();
        const resultRow = findResultForType(resultsList, nachRequestType, emiDate);
        const associates = toPlainJs(caseAssociates) || [];
        const mainBorrower = associates.find((a) => a.is_main_borrower);
        const formValues = this.state.formValuesBySection[nachRequestType];

        const values =
            formValues != null
                ? Map(formValues)
                : resultRow && resultRow.nach_details
                ? Map(resultRow.nach_details)
                : Map({
                      mandate_type: getDefaultMandateTypeFromCategory(category),
                      associate:
                          mainBorrower && mainBorrower.company_associate
                              ? mainBorrower.company_associate.id
                              : null,
                  });

        const instalmentFrequency =
            status.get('instalment_frequency') != null
                ? status.get('instalment_frequency')
                : journey.instalment_frequency;

        const sanctionLimit = caseDetail?.get
            ? caseDetail.get('sanction_limit')
            : caseDetail?.sanction_limit;
        const isLoanAbove1Cr = isLoanAbove1CrFromSanctionLimit(sanctionLimit);

        return (
            <Row key={nachRequestType} className="p-5">
                <Card>
                    <NachMandateForm
                        caseId={caseId}
                        bank_account={toPlainJs(status.get('bank_account'))}
                        nachRequestType={nachRequestType}
                        sectionTitle={getCategoryDisplayLabel(nachRequestType)}
                        sectionSubtitle={getCategorySubtitle(
                            nachRequestType,
                            journey,
                            emiList,
                            category
                        )}
                        categoryJourney={category}
                        resultRow={resultRow}
                        emiDate={emiDate}
                        isTermLoan={journey.is_term_loan}
                        instalmentFrequency={instalmentFrequency}
                        totalEmiCount={resolveTotalEmiCount(
                            journey,
                            resultsList,
                            resultRow,
                            emiList.length
                        )}
                        isLoanAbove1Cr={isLoanAbove1Cr}
                        emiCount={emiList.length || 1}
                        emiDates={emiList}
                        schemeType={schemeType}
                        caseAssociates={associates}
                        onSubmit={(payload, fv) => this.onSubmit(payload, fv, nachRequestType)}
                        values={values}
                        loading={
                            this.state.triggerDetails.nach_request_type === nachRequestType &&
                            nachData.get('status') === STATE_STATUS.FETCHING
                        }
                        triggerEnach={this.triggerEnach}
                        updateNachData={() => this.loadStatus(emiDate)}
                        currentCaseTaskStageId={currentCaseTaskStageId}
                        user={user}
                        documentUploadStatus={resultRow && resultRow.document_upload_status}
                    />
                </Card>
            </Row>
        );
    };

    renderNachSections = () => {
        const status = this.nachStatus();
        const journey = this.getJourney();

        if (status.get('is_backend_nach_activation_enabled') === false) {
            return (
                <Alert
                    type="info"
                    className="mt-10"
                    showIcon
                    message="Backend activation flag is OFF — EMI NACH and Security NACH sections are hidden for this tenant."
                />
            );
        }

        if (!journey || !journey.categories) {
            return (
                <Alert
                    type="warning"
                    className="mt-10"
                    showIcon
                    message="NACH journey data is not available. Ensure GET /enach-status/ returns nach_journey."
                />
            );
        }

        const requiredList = toPlainJs(status.get('required_nach_types')) || [];
        const instalmentFrequency =
            status.get('instalment_frequency') != null
                ? status.get('instalment_frequency')
                : journey.instalment_frequency;
        return getDesignSectionTypes(journey, requiredList, instalmentFrequency).map((type) =>
            this.renderNachSection(type)
        );
    };

    render() {
        const { nachData } = this.props;
        const status = this.nachStatus();
        const isFetching = status && status.get('status') === STATE_STATUS.FETCHING;

        return (
            <React.Fragment>
                <Row>
                    <Col span={24}>{this.getBankDetails()}</Col>
                </Row>
                <Spin spinning={this.state.visible && isFetching}>
                    {this.state.visible && this.isFetched() && (
                        <NachDetailsDrawer
                            triggerNachLoader={this.state.triggerNachLoader}
                            visible={this.state.visible}
                            nachData={nachData}
                            nachStatusResponse={this.findDrawerResponse()}
                            onClose={this.onClose}
                            triggerEnach={this.triggerEnach}
                        />
                    )}
                    {this.isFetched() && this.renderNachSections()}
                    {/* {this.isFetched() && this.renderProceedValidation()} */}
                </Spin>
            </React.Fragment>
        );
    }
}

NachMandate.propTypes = {
    caseId: PropTypes.number,
    companyId: PropTypes.number,
    schemeType: PropTypes.string,
    caseDetail: PropTypes.object,
};

const mapStateToProps = (state, props) => ({
    nachData: state.caseDetails.getIn([Number(props.caseId), 'nachMandate']),
    nachMandateStatusResponse: state.caseDetails.getIn(
        [Number(props.caseId), 'nachStatusResponse'],
        Map()
    ),
    caseAssociates: state.caseDetails.getIn([props.caseId, 'caseAssociates', 'data']),
    currentCaseTaskStageId: state.caseDetails.getIn([Number(props.caseId), 'data', 'current_case_task']),
    user: state.user.get('data'),
});

export default connect(mapStateToProps, (dispatch) =>
    bindActionCreators(
        {
            fetchCaseEnachMandataDetails,
            fetchCaseEnachMandateStatus,
        },
        dispatch
    )
)(NachMandate);
